import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
} from "@fluentui/react-components";
import { VSpace } from "components/Spacer";
import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import { useAppStoreState } from "store/AppStore";
import { useInsurerReportsStore } from "store/InsurerReportsStore";

type FieldFormat = "text" | "number" | "date" | "currency";

type FieldDef = {
  key: string;
  labelKey: string;
  format: FieldFormat;
};

const commonFields: FieldDef[] = [
  { key: "clientName", labelKey: "pct.clientName.label", format: "text" },
  { key: "policyNumber", labelKey: "pct.policyNumber.label", format: "text" },
  { key: "policyName", labelKey: "pct.policyName.label", format: "text" },
  { key: "insurerName", labelKey: "pct.insurer.label", format: "text" },
  { key: "currencyCode", labelKey: "pct.currency.label", format: "currency" },
  { key: "premiumAmount", labelKey: "pct.premiumAmount.label", format: "number" },
  { key: "commissionRate", labelKey: "pct.commissionRate.label", format: "number" },
  { key: "commissionAmount", labelKey: "pct.commissionAmount.label", format: "number" },
  { key: "bookingDate", labelKey: "pct.bookingDate.label", format: "date" },
];

const paymentPlanEntryFields: FieldDef[] = [
  ...commonFields,
  { key: "premiumFrom", labelKey: "pct.premiumFrom.label", format: "date" },
  { key: "premiumTo", labelKey: "pct.premiumTo.label", format: "date" },
  { key: "totalPremiumAmount", labelKey: "pct.totalPremiumAmount.label", format: "number" },
  { key: "paidPremiumAmount", labelKey: "pct.paidPremiumAmount.label", format: "number" },
  { key: "premiumDebt", labelKey: "pct.premiumDebt.label", format: "number" },
];

const paymentFields: FieldDef[] = [
  ...commonFields,
  { key: "paymentDate", labelKey: "pct.paymentDate.label", format: "date" },
  { key: "declaredAt", labelKey: "pct.declaredAt.label", format: "date" },
  { key: "paymentComment", labelKey: "pct.paymentComment.label", format: "text" },
];

export const InfoMatchedTargetDialog = () => {
  const [
    {
      infoInsurerReportEntryDialog,
      infoInsurerReportEntryStatus,
      infoMatchedTarget,
      infoMatchedTargetKind,
    },
    { setInsurerReportsStoreValue },
  ] = useInsurerReportsStore();
  const { taxonomy } = useAppStoreState();
  const { t } = useTranslation();

  const isLoading = infoInsurerReportEntryStatus === "loading";

  const numberFormat = new Intl.NumberFormat(window.navigator.languages[0], {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const formatValue = (value: any, format: FieldFormat): string => {
    if (value === null || value === undefined || value === "") return "";
    switch (format) {
      case "date":
        return moment(value).format("DD.MM.YYYY");
      case "number":
        return isNaN(Number(value)) ? String(value) : numberFormat.format(Number(value));
      case "currency": {
        const item = taxonomy?.Currency?.byId?.[Number(value)];
        return item ? t(item.code) : String(value);
      }
      default:
        return String(value);
    }
  };

  const fields =
    infoMatchedTargetKind === "payment" ? paymentFields : paymentPlanEntryFields;

  const rows = infoMatchedTarget
    ? fields
        .map((f) => {
          let value = formatValue(infoMatchedTarget[f.key], f.format);
          if (f.key === "clientName" && value !== "") {
            const internal = infoMatchedTarget["clientInternalNumber"];
            if (internal !== null && internal !== undefined) {
              value += ` (${internal})`;
            }
          }
          return { label: t(f.labelKey), value };
        })
        .filter((r) => r.value !== "")
    : [];

  return (
    <Dialog open={infoInsurerReportEntryDialog.isOpen}>
      <DialogSurface data-help="insurer-reports-info-dialog">
        <DialogBody>
          <DialogTitle>
            {t("pct.matchedTargetInfo.label")}
            {infoMatchedTargetKind
              ? " - " +
                t(
                  infoMatchedTargetKind === "payment"
                    ? "pct.payment.label"
                    : "pct.paymentPlanEntry.label"
                )
              : ""}
          </DialogTitle>
          <DialogContent>
            <VSpace height={10} />
            {isLoading ? (
              <Spinner size="tiny" />
            ) : (
              <div style={{ maxHeight: 450, overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.label}>
                        <td
                          style={{
                            padding: "4px 8px",
                            fontWeight: 600,
                            verticalAlign: "top",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.label}
                        </td>
                        <td style={{ padding: "4px 8px" }}>{row.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <VSpace height={10} />
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              onClick={() => {
                setInsurerReportsStoreValue("infoInsurerReportEntryDialog", {
                  isOpen: false,
                  insurerReportEntryId: null,
                });
              }}
            >
              {t("pct.close.label")}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
